import { BookOpen, GraduationCap, Loader2, Lock, Plus, Search, Trash2 } from 'lucide-react';
import ConfirmModal from '../../../components/modals/ConfirmModal';
import React, { useEffect, useMemo, useState } from 'react';
import { ApiService } from '../../../services/api';

interface Teacher {
  id: string;
  planTeacherId?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  email?: string;
  branchAssignments?: TeacherBranchAssignment[];
}

interface Lesson {
  id: string;
  name: string;
  code?: string;
}

interface TeacherBranchAssignment {
  id: string;
  teacherId: string;
  teacher?: Teacher;
  lessonId: string;
  lesson?: Lesson;
}

const normalizeSearchText = (value?: string | null) =>
  (value || "")
    .toLocaleLowerCase("tr")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ı/g, "i")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ş/g, "s")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .replace(/\s+/g, " ")
    .trim();

const getEditDistance = (left: string, right: string, maxDistance = 1) => {
  if (Math.abs(left.length - right.length) > maxDistance) return maxDistance + 1;

  const previous = Array.from({ length: right.length + 1 }, (_, index) => index);

  for (let i = 1; i <= left.length; i += 1) {
    let lastDiagonal = previous[0];
    previous[0] = i;
    let rowMin = previous[0];

    for (let j = 1; j <= right.length; j += 1) {
      const temp = previous[j];
      previous[j] = left[i - 1] === right[j - 1]
        ? lastDiagonal
        : Math.min(lastDiagonal, previous[j - 1], previous[j]) + 1;
      lastDiagonal = temp;
      rowMin = Math.min(rowMin, previous[j]);
    }

    if (rowMin > maxDistance) return maxDistance + 1;
  }

  return previous[right.length];
};

const teacherMatchesSearch = (teacher: Teacher, search: string) => {
  const normalizedSearch = normalizeSearchText(search);
  if (!normalizedSearch) return true;

  const teacherText = normalizeSearchText([
    teacher.name,
    teacher.firstName,
    teacher.lastName,
    teacher.email,
  ].filter(Boolean).join(" "));

  if (teacherText.includes(normalizedSearch)) return true;

  const teacherWords = teacherText.split(" ").filter(Boolean);
  return normalizedSearch.split(" ").filter(Boolean).every((token) =>
    teacherWords.some((word) =>
      word.includes(token) ||
      token.includes(word) ||
      (token.length >= 4 && getEditDistance(token, word, 1) <= 1)
    )
  );
};

const TeacherBranchAssignments = () => {
  const [searchTeacher, setSearchTeacher] = useState("");
  const [selectedTeacher, setSelectedTeacher] = useState<string | null>(null);
  const [selectedLesson, setSelectedLesson] = useState<string | null>(null);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [assignments, setAssignments] = useState<TeacherBranchAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState<string | null>(null);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchAssignments();
  }, [selectedTeacher, selectedLesson]);

  const parseListResponse = (res: any) => {
    const data = (res?.data ?? res) as any;

    if (Array.isArray(data)) return data;
    if (data?.data && Array.isArray(data.data)) return data.data;
    if (data?.results && Array.isArray(data.results)) return data.results;

    return [];
  };

  const fetchInitialData = async () => {
    try {
      setLoading(true);

      await Promise.all([fetchLessons(), fetchTeachers(), fetchAssignments()]);
    } finally {
      setLoading(false);
    }
  };

  const fetchLessons = async () => {
    try {
      const res = await ApiService.getPlanningLessons({ limit: 100, isActive: true });

      setLessons(parseListResponse(res));
    } catch (error) {
      console.error("Dersler yüklenemedi:", error);

      setLessons([]);
    }
  };

  const fetchTeachers = async () => {
    try {
      const res = await ApiService.getTeacherBranchAssignmentTeachers();

      setTeachers(parseListResponse(res));
    } catch (error) {
      console.error("Öğretmenler yüklenemedi:", error);

      setTeachers([]);
    }
  };

  const fetchAssignments = async () => {
    try {
      setAssignmentsLoading(true);

      const res = await ApiService.getTeacherBranchAssignments({
        limit: 100,
        teacherId: selectedTeacher || undefined,
        lessonId: !selectedTeacher ? selectedLesson || undefined : undefined,
      });

      setAssignments(parseListResponse(res));
    } catch (error) {
      console.error("Branş atamaları yüklenemedi:", error);

      setAssignments([]);
    } finally {
      setAssignmentsLoading(false);
    }
  };

  const getTeacherName = (teacher?: Teacher) => {
    if (!teacher) return "İsimsiz";
    if (teacher.firstName && teacher.lastName) return `${teacher.firstName} ${teacher.lastName}`;

    return teacher.name || "İsimsiz";
  };

  const createAssignment = async () => {
    if (!selectedTeacher || !selectedLesson) return;

    setSubmitting(true);
    try {
      await ApiService.createTeacherBranchAssignment({
        teacherId: selectedTeacher,
        lessonId: selectedLesson
      });

      await Promise.all([fetchTeachers(), fetchAssignments()]);
    } catch (error: any) {
      console.error("Branş ataması oluşturulamadı:", error);

      setErrorMessage(error.response?.data?.message || "Branş ataması oluşturulurken bir hata oluştu.");
      setErrorModalOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedTeacherData = teachers.find((teacher) => teacher.id === selectedTeacher);
  const selectedTeacherLessonIds = new Set(
    (selectedTeacherData?.branchAssignments || []).map((assignment) => assignment.lessonId)
  );
  const visibleTeachers = useMemo(
    () => teachers.filter((teacher) => teacherMatchesSearch(teacher, searchTeacher)),
    [teachers, searchTeacher]
  );
  const visibleAssignments = useMemo(() => {
    if (selectedTeacherData) {
      return (selectedTeacherData.branchAssignments || []).map((assignment) => ({
        ...assignment,
        teacherId: selectedTeacherData.planTeacherId || selectedTeacherData.id,
        teacher: selectedTeacherData,
      }));
    };

    if (selectedLesson) return assignments.filter((assignment) => assignment.lessonId === selectedLesson);
    return assignments;
  }, [assignments, selectedLesson, selectedTeacherData]);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="mb-6">
          <p className="text-sm text-gray-600">Öğretmenlere girebilecekleri dersleri sınıf bağı olmadan tanımlayın</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-gray-50 rounded-lg p-4 h-full flex flex-col">
            <div className="mb-4">
              <h3 className="font-semibold text-gray-900 mb-3">Öğretmen Seçimi</h3>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Öğretmen ara..."
                  value={searchTeacher}
                  onChange={(e) => setSearchTeacher(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="space-y-2 flex-1 overflow-y-auto max-h-[520px]">
              {loading ? (
                <div className="text-center p-4">
                  <Loader2 className="animate-spin mx-auto" />
                </div>
              ) : visibleTeachers.map((teacher) => (
                <div
                  key={teacher.id}
                  className={`p-3 rounded-lg cursor-pointer transition-colors border ${selectedTeacher === teacher.id
                    ? "bg-blue-100 border-blue-200 text-blue-800"
                    : "bg-white border-gray-200 hover:bg-gray-50"
                    }`}
                  onClick={() => setSelectedTeacher((prev) => prev === teacher.id ? null : teacher.id)}
                >
                  <div className="flex items-center space-x-2 mb-1">
                    <GraduationCap size={16} className="text-gray-500" />
                    <span className="font-medium">{getTeacherName(teacher)}</span>
                  </div>
                  <div className="text-xs text-blue-600 ml-6">
                    Branş Sayısı: {teacher.branchAssignments?.length || 0}
                  </div>
                </div>
              ))}

              {!loading && visibleTeachers.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <GraduationCap size={40} className="mx-auto mb-3 text-gray-300" />
                  <p>Öğretmen bulunamadı</p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 h-full flex flex-col">
            <h3 className="font-semibold text-gray-900 mb-3">Ders Seçimi</h3>
            <div className="space-y-2 flex-1 overflow-y-auto max-h-[520px]">
              {loading ? (
                <div className="text-center p-4">
                  <Loader2 className="animate-spin mx-auto" />
                </div>
              ) : lessons.map((lesson) => {
                const isAssignedToSelectedTeacher = !!selectedTeacher && selectedTeacherLessonIds.has(lesson.id);
                return (
                  <div
                    key={lesson.id}
                    className={`p-3 rounded-lg transition-colors border ${isAssignedToSelectedTeacher
                      ? "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed"
                      : selectedLesson === lesson.id
                        ? "bg-green-100 border-green-200 text-green-800 cursor-pointer"
                        : "bg-white border-gray-200 hover:bg-gray-50 cursor-pointer"
                      }`}
                    onClick={() => {
                      if (isAssignedToSelectedTeacher) return;
                      setSelectedLesson((prev) => prev === lesson.id ? null : lesson.id);
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <BookOpen size={16} className={isAssignedToSelectedTeacher ? "text-gray-400" : "text-blue-500"} />
                        <span className={`font-medium ${isAssignedToSelectedTeacher ? "text-gray-500" : "text-gray-900"}`}>{lesson.name}</span>
                      </div>
                      {isAssignedToSelectedTeacher && (
                        <div className="flex items-center space-x-1 text-xs text-gray-500">
                          <Lock size={12} />
                          <span>Atanmış</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-4">
              <button
                onClick={createAssignment}
                disabled={!selectedTeacher || !selectedLesson || submitting}
                className={`w-full flex items-center justify-center space-x-2 py-2 px-4 rounded-lg transition-colors ${selectedTeacher && selectedLesson
                  ? "bg-green-600 hover:bg-green-700 text-white"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
              >
                {submitting ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />}
                <span>Branş Ataması Yap</span>
              </button>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 h-full flex flex-col">
            <div className="mb-3">
              <h3 className="font-semibold text-gray-900">Mevcut Atamalar</h3>
              <p className="text-xs text-gray-500 mt-1">
                {selectedTeacherData
                  ? `${getTeacherName(selectedTeacherData)} için tanımlı branşlar`
                  : selectedLesson
                    ? "Bu dersi verebilen öğretmenler"
                    : "Tüm branş atamaları"}
              </p>
            </div>

            <div className="space-y-3 flex-1 overflow-y-auto max-h-[520px]">
              {assignmentsLoading ? (
                <div className="text-center p-4">
                  <Loader2 className="animate-spin mx-auto text-gray-400" />
                </div>
              ) : visibleAssignments.map((assignment) => (
                <div key={assignment.id} className="bg-white rounded-lg p-3 border border-gray-200">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center space-x-2 mb-1">
                        <GraduationCap size={14} className="text-blue-500" />
                        <span className="font-medium text-gray-900 text-sm">
                          {getTeacherName(assignment.teacher)}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <BookOpen size={12} className="text-gray-400" />
                        <span className="text-xs text-gray-600">
                          {assignment.lesson?.name || "Ders Silinmiş"}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setAssignmentToDelete(assignment.id);
                        setDeleteModalOpen(true);
                      }}
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Atamayı kaldır"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}

              {!assignmentsLoading && visibleAssignments.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <GraduationCap size={48} className="mx-auto mb-3 text-gray-300" />
                  <p>Henüz branş ataması yapılmamış</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setAssignmentToDelete(null);
        }}
        onConfirm={async () => {
          if (!assignmentToDelete) return;
          try {
            await ApiService.deleteTeacherBranchAssignment(assignmentToDelete);
            await Promise.all([fetchTeachers(), fetchAssignments()]);
          } catch (error: any) {
            setErrorMessage(error.response?.data?.message || "Atama silinirken hata oluştu.");
            setErrorModalOpen(true);
          } finally {
            setDeleteModalOpen(false);
            setAssignmentToDelete(null);
          }
        }}
        title="Branş Atamasını Kaldır"
        message="Bu branş atamasını kaldırmak istediğinize emin misiniz?"
        confirmText="Kaldır"
        cancelText="Vazgeç"
        confirmButtonVariant="danger"
      />

      <ConfirmModal
        isOpen={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        onConfirm={() => setErrorModalOpen(false)}
        title="Hata"
        message={errorMessage}
        confirmText="Tamam"
        cancelText=""
        confirmButtonVariant="primary"
      />
    </div>
  );
};

export default TeacherBranchAssignments;
